<cs-header>
  <cs-header-title class="title-with-menu">
    {{ title }}
    @if (canCheckForUpdate()) {
      <cs-header-title-action>
        <cs-icon-button icon="ellipsis-vertical" (click)="handleHeaderMenuClick($event)"></cs-icon-button>
      </cs-header-title-action>
    }
  </cs-header-title>
</cs-header>

<cs-content [loading]="loading">
  <input id="hidden-input" style="visibility: hidden; position: absolute;" type="file" accept="image/png, image/jpeg"
    (change)="onImageUploadEvent($event)" />

  @if (exerciseNotFound) {
    <cs-info-card type="warning" [dismissible]="false">
      {{ LL.editExercise.messages.exerciseNotFound() }}
    </cs-info-card>
  }

  <!-- #4737: the database could not be read, which is NOT the same as the exercise being
       gone — saying so would send the user off to recreate one they still have. -->
  @if (exerciseUnreadable) {
    <cs-info-card type="warning" [dismissible]="false">
      {{ LL.editExercise.messages.exerciseUnreadable() }}
    </cs-info-card>
  }

  @if (isFromFormerCoach) {
    <!-- Phase 3 of #3198 — "Former coach" indicator. Rendered as the FIRST card
         inside <cs-content>, BEFORE the imported-exercise chain, so a UDE that's
         simultaneously imported, has a pending update, AND came from a former
         coach stacks the cards in the order: former-coach, then the
         imported-exercise chain. Unconditional on hasSourceLineage so from-scratch
         coach UDEs (no local.sourceExerciseId) still display the indicator. Uses
         a dedicated editPageBody string (NOT the picker's tooltip text, which
         ends with "pick a different version" — incongruous on the edit page where
         the user is already editing). -->
    <cs-info-card type="info" [dismissible]="false" class="share-banner">
      {{ formerCoachBadgeBodyText() }}
    </cs-info-card>
  }

  @if (isImportedExercise) {
    @if (hasOriginalUpdate) {
      <cs-info-card type="info" [dismissible]="false" [actionText]="LL.editExercise.buttons.refreshFromOriginal()"
        (actionClick)="handleRefreshFromOriginal()" class="share-banner">
        {{ LL.editExercise.messages.updateAvailable() }}
      </cs-info-card>
    } @else if (originalDeleted) {
      <cs-info-card type="warning" [dismissible]="false" class="share-banner">
        {{ LL.editExercise.messages.originalUnavailable() }}
      </cs-info-card>
    } @else if (hasSourceLineage && sourceExerciseName) {
      <!-- combined fork card: "Based on: <name>" body + optional action.
           Single [actionText] slot → "Revert to Original" when revertable, else no action.
           "Check for Update" is NOT here: a partial apply / ignore is treated as resolved
           (no "pending" state), and the manual re-pull lives in the header ⋮ menu. A
           genuinely-new coach rev surfaces via the "Update Available" banner above. -->
      <cs-info-card type="info" [dismissible]="false" [hideIcon]="true" class="share-banner"
        [actionText]="basedOnCardActionText()"
        (actionClick)="handleBasedOnCardAction()">
        <button class="link" (click)="handleViewSourceClick()">{{ LL.editExercise.messages.basedOn({ name: sourceExerciseName }) }}</button>
      </cs-info-card>
    } @else {
      <!-- legacy import (no upstream lineage): keep today's "Imported Exercise" badge -->
      <cs-info-card type="info" [dismissible]="false" [hideIcon]="true" class="share-banner">
        {{ LL.editExercise.messages.importedExercise() }}
      </cs-info-card>
    }
  }

  @if (exercise) {
    <div class="form-content">
      <!-- Basic Fields Row -->
      <div class="form-row">
        <cs-form-field [label]="LL.editExercise.labels.name()" class="name-field">
          <cs-input [(value)]="exercise.name" [placeholder]="LL.editExercise.placeholders.exerciseName()"
            autocomplete="off" (input)="nameChanged()" />
        </cs-form-field>
        <cs-form-field [label]="LL.editExercise.labels.equipmentTemplate()" class="equipment-field">
          <cs-select [placeholder]="LL.editExercise.placeholders.selectTemplate()"
            [(value)]="exercise.equipmentTemplateId" (valueChange)="somethingChanged()">
            <!-- template.name is a localized equipment-template display name (already
                 routed through LL by localizeEquipmentTemplates); Equipment.name itself
                 is a shared data-model field typed string, not display prose to translate here -->
            @for (template of equipmentTemplates; track template) {
              <cs-select-option [value]="template.id"
              [label]="template.name | untranslated"></cs-select-option>
            }
          </cs-select>
        </cs-form-field>
      </div>
      <!-- Advanced Settings Section -->
      <div class="advanced-section">
        @if (mode !== Mode.Admin) {
          <cs-button tertiary [icon-right]="isAdvancedExpanded ? 'arrow-dropup' : 'arrow-dropdown'" (click)="toggleAdvanced()">{{ LL.editExercise.labels.advancedSettings() }}</cs-button>
        }
        @if (isAdvancedExpanded || mode === Mode.Admin) {
          <div class="advanced-content">
            <div class="form-row">
              <!-- Short Name (optional) -->
              <cs-form-field [label]="LL.editExercise.labels.shortName()" class="shortname-field">
                <cs-input [(value)]="exercise.shortname"
                  [placeholder]="LL.editExercise.placeholders.shortName()" autocomplete="off"
                  (valueChange)="somethingChanged()" />
              </cs-form-field>
              <!-- Bodyweight % -->
              <cs-form-field class="bodyweight-field">
                <cs-label>{{ LL.editExercise.labels.bodyweightPercent() }}</cs-label>
                <cs-input-measure type="percent" [(canonValue)]="exercise.bw_pc"
                (canonValueChange)="somethingChanged()"></cs-input-measure>
                <span class="field-hint">{{ LL.editExercise.hints.bodyweightPercent() }}</span>
              </cs-form-field>
            </div>
            <!-- Most Similar Exercise (custom mode only) -->
            @if (mode !== Mode.Admin) {
              <cs-form-field [label]="LL.editExercise.labels.similarExercise()" class="similar-exercise-field">
                <cs-select [placeholder]="LL.editExercise.placeholders.selectExercise()"
                  [(value)]="customExercise.similarExerciseId" (valueChange)="somethingChanged()"
                  [searchable]="true">
                  <!-- e.name is an exercise-catalog name, out of scope for i18n per this
                       phase's PRD (database content localization is a separate project) -->
                  @for (e of allExercises; track e) {
                    <cs-select-option [value]="e._id" [label]="e.name | untranslated"></cs-select-option>
                  }
                </cs-select>
                <span class="field-hint">{{ LL.editExercise.hints.similarExercise() }}</span>
              </cs-form-field>
            }
            <!-- Video URL field (all modes) -->
            <cs-form-field [label]="LL.editExercise.labels.videoUrl()" class="video-url-field">
              <cs-input [(value)]="exercise.videoUrl" [placeholder]="LL.editExercise.placeholders.videoUrl()"
                autocomplete="off" (valueChange)="somethingChanged()" />
            </cs-form-field>
            <!-- Instructions field (all modes) -->
            <cs-form-field [label]="LL.editExercise.labels.instructions()" class="description-field">
              <cs-textarea #descriptionTextarea [placeholder]="LL.editExercise.placeholders.instructions()"
                [(value)]="exercise.description" (valueChange)="somethingChanged()" />
              <div class="description-actions">
                @if (mode === Mode.Admin) {
                  <cs-button tertiary icon="add" (click)="addImageButtonClick()">
                    {{ LL.editExercise.buttons.addImage() }}
                  </cs-button>
                }
                <cs-button tertiary icon="eye" (click)="previewDescriptionButtonClick()">
                  {{ LL.editExercise.buttons.previewInstructions() }}
                </cs-button>
              </div>
            </cs-form-field>
            <!-- Admin-only fields -->
            @if (mode === Mode.Admin) {
              <div class="form-row">
                <cs-form-field [label]="LL.editExercise.labels.id()" class="id-field">
                  <cs-input [disabled]="true" type="text" [(value)]="exercise._id"></cs-input>
                </cs-form-field>
              </div>
              <div class="form-row toggle-row">
                <div class="toggle-field">
                  <cs-label>{{ LL.editExercise.labels.myoRep() }}</cs-label>
                  <cs-toggle [(checked)]="exercise.allowMyoReps" (checkedChange)="somethingChanged()"></cs-toggle>
                </div>
                <div class="toggle-field">
                  <cs-label>{{ LL.editExercise.labels.rmTesting() }}</cs-label>
                  <cs-toggle [checked]="!exercise.avoidRMTesting"
                  (checkedChange)="useRMTestingChanged($event)"></cs-toggle>
                </div>
              </div>
              <div class="standard-buttons">
                <cs-button tertiary icon="add" (click)="addStandardAbsolute()">
                  {{ LL.editExercise.buttons.addAbsoluteStandard() }}
                </cs-button>
                <cs-button tertiary icon="add" (click)="addStandardRelative()">
                  {{ LL.editExercise.buttons.addRelativeStandard() }}
                </cs-button>
              </div>
            }
          </div>
        }
      </div>
      <!-- Strength Standard Cards (Admin only) -->
      @if (mode === Mode.Admin) {
        <!-- Relative Strength Standard Card -->
        @if (exercise.standard && isStrengthStandardRelative(exercise.standard)) {
          <cs-card class="standard-card">
            <cs-card-title>{{ LL.editExercise.titles.relativeStrengthStandard() }}</cs-card-title>
            <cs-card-content>
              <div class="form-row">
                <cs-form-field [label]="LL.editExercise.labels.referenceExercise()">
                  <cs-select [placeholder]="LL.editExercise.placeholders.selectExercise()"
                    [(value)]="exercise.standard.idReferenceExercise" (valueChange)="somethingChanged()"
                    [searchable]="true">
                    <!-- e.name is an exercise-catalog name, out of scope for i18n per this
                         phase's PRD (database content localization is a separate project) -->
                    @for (e of allExercises; track e) {
                      <cs-select-option [value]="e._id" [label]="e.name | untranslated"></cs-select-option>
                    }
                  </cs-select>
                </cs-form-field>
                <cs-form-field [label]="LL.editExercise.labels.oneRMFactor()">
                  <cs-input-measure type="relative_factor" [(canonValue)]="exercise.standard.factor1RM_pc"
                  (canonValueChange)="somethingChanged()"></cs-input-measure>
                </cs-form-field>
              </div>
            </cs-card-content>
          </cs-card>
        }
        <!-- Absolute Strength Standard Cards -->
        @if (exercise.standard && !isStrengthStandardRelative(exercise.standard)) {
          @for (g of genders; track g) {
            <cs-card class="standard-card">
              <cs-card-title>{{ LL.editExercise.titles.absoluteStrengthStandard({gender: g}) }}</cs-card-title>
              <cs-card-content>
                <cs-grid>
                  <cs-grid-row>
                    <cs-grid-col>
                      <cs-label text-right>{{ LL.editExercise.labels.bodyWeight() }}</cs-label>
                    </cs-grid-col>
                    @for (l of levels; track l) {
                      <cs-grid-col>
                        <cs-label text-right>{{ l.name }}</cs-label>
                      </cs-grid-col>
                    }
                  </cs-grid-row>
                  @for (perBW of exercise.standard[g]; track perBW) {
                    <cs-grid-row>
                      <cs-grid-col>
                        <cs-input-measure type="bodyweight_kg" [(canonValue)]="perBW.bw_kg"
                        (canonValueChange)="somethingChanged()"></cs-input-measure>
                      </cs-grid-col>
                      @for (sc of perBW.classes; track sc) {
                        <cs-grid-col>
                          <cs-input-measure type="addedweight_kg" [(canonValue)]="sc.min1rm_kg"
                          (canonValueChange)="somethingChanged()"></cs-input-measure>
                        </cs-grid-col>
                      }
                      <cs-grid-col>
                        <cs-icon-button icon="trash" (click)="removeStandardRow(exercise.standard[g], perBW)"></cs-icon-button>
                      </cs-grid-col>
                    </cs-grid-row>
                  }
                  <cs-grid-row>
                    <cs-button tertiary icon="add" (click)="addStandardRow(exercise.standard[g])">
                      {{ LL.editExercise.buttons.addWeightCategory() }}
                    </cs-button>
                  </cs-grid-row>
                </cs-grid>
              </cs-card-content>
            </cs-card>
          }
        }
      }
    </div>
  }
</cs-content>

<cs-page-action-buttons [showCancel]="isButtonVisible(CsHeaderButtons.Cancel)"
  (cancel)="handleCancelButtonClicked($event, ButtonType.Footer)">
  @if (isButtonVisible(CsHeaderButtons.Accept)) {
    <cs-button primary medium
      [label]="LL.common.buttons.save()"
    (click)="handleAcceptButtonClicked($event, ButtonType.Footer)"></cs-button>
  }
</cs-page-action-buttons>
